import * as React from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import { Box } from "@mui/material";
import { energyProjectTypes, fundingList, statusList, transmissionProjectTypes } from "../TableData";

dayjs.extend(utc);

function getProjectTypeSelectOptions(AllLists: any) {
    return AllLists?.executeGetRawProjectTypes.map((type) => ({
        value: type.RawProjectType,
        text: type.RawProjectType
    })) ?? []
}

export function statePriorYearBaselineConfig(AllLists: any, getCommonEditTextFieldProps): any[] {
    const customParseFormat = require('dayjs/plugin/customParseFormat')
    dayjs.extend(customParseFormat)
    const minConstructionDate = dayjs('2015', 'YYYY')
    const maxConstructionDate = dayjs('2040', 'YYYY')
    return [
        {
            accessorKey: "ProjectID",
            header: "Original Project ID",
            size: 200,
            filterFn: "contains"
        },

        {
            accessorKey: "ProjectName",
            header: "Project name",
            size: 200,
            filterFn: "contains",
            muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
                ...getCommonEditTextFieldProps(cell)
            }),
            Cell: ({ cell }) => {
                const cellValue = cell.getValue();
                return <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", backgroundColor: !cellValue ? "orange" : "" }}>
                    {
                        // @ts-ignore
                        !cellValue ? "Please enter non-empty project name" : cellValue
                    }
                </Box>;
            },
        },
        {
            accessorKey: "State",
            header: "State",
            size: 100,
            filterVariant: "multi-select",
            editVariant: "select",
            // @ts-ignore
            editSelectOptions: AllLists?.executeGetState.map((state) => ({
                value: state.State,
                text: state.State
            })) ?? []
        },
        {
            accessorKey: "Source",
            header: "Source",
            size: 150,
            filterVariant: "multi-select",
            editVariant: "select",
            enableEditing: false,
            // @ts-ignore
            editSelectOptions: AllLists?.executeGetSourceList.map((type) => ({
                value: type.Source,
                text: type.Source
            })) ?? []
        },
        {
            id: "MW",
            accessorFn: (row) => Number(row.MW),
            filterFn: "betweenInclusive",
            filterVariant: "range",
            header: "MW",
            enableEditing: (row) => energyProjectTypes.includes(row.getValue("typeCast")) ? true : false,
            size: 150,
            Cell: ({ cell }) => {
                return <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
                    {
                        // @ts-ignore
                        cell.getValue<number>().toLocaleString()
                    }
                </Box>;
            },
            muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
                ...getCommonEditTextFieldProps(cell)
            }),
        },
        {
            id: "KM",
            accessorFn: (row) => Number(row.KM),
            header: "KM",
            filterVariant: "range",
            filterFn: "betweenInclusive",
            size: 150,
            enableEditing: (row) => [transmissionProjectTypes[1], transmissionProjectTypes[2]].includes(row.getValue("typeCast")) ? true : false,
            Cell: ({ cell }) => {
                return <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
                    {
                        // @ts-ignore
                        cell.getValue<number>().toLocaleString()
                    }
                </Box>;
            }
        },
        {
            id: "M2",
            accessorFn: (row) => Number(row.M2),
            header: "M2",
            filterVariant: "range",
            filterFn: "betweenInclusive",
            size: 150,
            Cell: ({ cell }) => {
                return <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
                    {
                        // @ts-ignore
                        cell.getValue<number>().toLocaleString()
                    }
                </Box>;
            }
        },
        {
            id: "Investment",
            accessorFn: (row) => row.Investment,
            filterFn: "betweenInclusive",
            filterVariant: "range",
            header: "Investment ($)",
            Cell: ({ cell }) => {
                const cellValue = cell.getValue();
                return <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", backgroundColor: !cellValue ? "orange" : "" }}>
                    {
                        // @ts-ignore

                        cellValue ? cellValue : "Please enter non-empty investment value"
                    }
                </Box>;
            },
            size: 200,
            muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
                ...getCommonEditTextFieldProps(cell)
            }),
        },
        {
            accessorKey: "typeCast",
            header: "Project type",
            size: 200,
            filterVariant: "multi-select",
            editVariant: "select",
            // @ts-ignore
            editSelectOptions: getProjectTypeSelectOptions(AllLists),
            muiTableBodyCellEditTextFieldProps: ({ cell }) => ({
                ...getCommonEditTextFieldProps(cell)
            }),
            Cell: ({ cell }) => {
                const cellValue = cell.getValue();
                return <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", background: !cellValue ? "orange" : "" }}>
                    {
                        // @ts-ignore
                        !cellValue ? "Please enter non-empty project type" : cellValue
                    }
                </Box>;
            },
        },
        {
            accessorKey: "ProjectStage",
            header: "Project latest status",
            size: 250,
            filterVariant: "multi-select",
            editVariant: "select",
            editSelectOptions: statusList
        },

        {
            accessorKey: "DeliveryAgency",
            header: "Delivery agency",
            size: 150,
            filterVariant: "range",
            filterFn: "betweenInclusive",
            enableEditing: true,
        },
        {
            accessorKey: "ContractingModel",
            header: "Contracting model",
            size: 150,
            filterVariant: "range",
            filterFn: "betweenInclusive",
            enableEditing: true,
        },
        {
            accessorKey: "Latitude",
            header: "Latitude",
            size: 150,
            filterFn: "betweenInclusive",
            filterVariant: "range",
            enableEditing: true,
        },
        {
            accessorKey: "Longitude",
            header: "Longitude",
            size: 150,
            filterFn: "betweenInclusive",
            filterVariant: "range",
            enableEditing: true,
        },
        {
            accessorKey: "sa4_code",
            header: "sa4_Code",
            size: 150,
            filterVariant: "range",
            filterFn: "betweenInclusive",
            enableEditing: true,
        },
        {
            accessorKey: "sa4_name",
            header: "sa4_Name",
            size: 150,
            filterVariant: "range",
            filterFn: "betweenInclusive",
            enableEditing: true,
        },
        {
            id: "PeriodStartYear",
            accessorFn: (row) => row.PeriodStartYear,
            header: "Period Start Year",
            size: 300,
            enableEditing: false,
            filterVariant: "range",
            filterFn: "betweenInclusive",
            sortingFn: "datetime",
            Cell: ({ cell }) => {
                return <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
                    {
                        // @ts-ignore
                        cell.getValue()
                    }
                </Box>;
            }, //render Date as a string

        },
        {
            id: "PeriodEndYear",
            accessorFn: (row) => row.PeriodEndYear,
            header: "Period End Year",
            Cell: ({ cell }) => {
                return <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
                    {
                        // @ts-ignore
                        cell.getValue()
                    }
                </Box>;
            }, //render Date as a string
            size: 300,
            enableEditing: false,
            filterVariant: "range",
            filterFn: "betweenInclusive",
            sortingFn: "datetime",


        },
        {
            id: "StartDateConstruction",
            accessorFn: (row) => row.StartDateConstruction,
            header: "Construction start date",
            size: 300,
            filterVariant: "range",
            filterFn: "betweenInclusive",
            sortingFn: "datetime",
            Cell: ({ cell }) => {
                const cellValue = cell.getValue();
                return <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", backgroundColor: cellValue === "" ? "orange" : "" }}>
                    {
                        // @ts-ignore
                        cellValue === "" ? "Please enter non-empty start date" : cellValue
                    }
                </Box>;
            }, //render Date as a string
        },
        {
            id: "EndDateConstruction",
            accessorFn: (row) => row.EndDateConstruction,
            header: "Construction end date",
            size: 300,
            filterVariant: "range",
            filterFn: "betweenInclusive",
            sortingFn: "datetime",
            // @ts-ignore
            Cell: ({ cell }) => {
                const cellValue = cell.getValue();
                return <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", backgroundColor: cellValue === "" ? "orange" : "" }}>
                    {
                        // @ts-ignore
                        cellValue === "" ? "Please enter non-empty end date" : cellValue
                    }
                </Box>;
            }, //render Date as a string
            //Custom Date Picker Filter from @mui/x-date-pickers

        },
        {
            accessorKey: "FundingMode",
            header: "Funding mode",
            size: 150,
            editVariant: "select",
            editSelectOptions: fundingList,
            enableEditing: true,
        },
        {
            accessorKey: "CommonwealthFunding",
            header: "Commonwealth funding",
            size: 200,
            editVariant: "select",
        },
        {
            id: "LocationID",
            accessKey: "project_location",
            header: "Location",
            size: 300,
            filterVariant: "multi-select",
            editVariant: "select",
            Cell: ({ cell }) => {
                return (
                    // @ts-ignore
                    AllLists?.executeGetLocationList.find(item => item.LocationID == cell.getValue())?.LocationName
                )

            },
            filterSelectOptions: AllLists?.executeGetLocationList.map((location) => ({
                value: location.LocationID,
                text: location.LocationName
            })) ?? [],
            // @ts-ignore
            editSelectOptions: AllLists?.executeGetLocationList.map((location) => ({
                value: location.LocationID,
                text: location.LocationName
            })) ?? []
        },
    ]
}
